import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { parseDateLocal } from "@/lib/utils";
import { format } from "date-fns";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { normalizePlanType, AgeBandType } from "@shared/schema";
import { useQuotePremiumCalculation } from "@/hooks/useQuotePremiumCalculation";
import {
  CA_RATING_AREAS,
  findAreaByZip,
  mapWordBrownAreaLabelToId,
} from "@/lib/caRatingAreas";
import type {
  Quote,
  Carrier,
  Client,
  Prospect,
  Employee,
  Dependent,
  PlanWithCarrier,
  DirectoryContact,
  GeneralAgencyWithCarriers,
} from "@shared/schema";
import { normalizeQuoteContribution, type QuoteData } from "./types";
import { QUOTE_STEPS, RFP_STEPS } from "./constants";
import { buildWizardState } from "@/lib/quoting/wizardState";
import { findGaSelectionProblems } from "@/lib/quoting/rfpGaSelection";

export type WizardMode = "quote" | "rfp";

export interface UseQuoteWizardProps {
  existingQuote?: Quote | null;
  mode?: WizardMode;
  initialStep?: number;
  initialClientId?: number | null;
  initialProspectId?: number | null;
  onStepChange?: (step: number) => void;
  onDraftIdChange?: (id: number | null) => void;
}

export function useQuoteWizard({
  existingQuote,
  mode = "quote",
  initialStep = 1,
  initialClientId,
  initialProspectId,
  onStepChange,
  onDraftIdChange,
}: UseQuoteWizardProps = {}) {
  const isRFPMode = mode === "rfp";
  const STEPS = isRFPMode ? RFP_STEPS : QUOTE_STEPS;
  const [currentStep, setCurrentStepState] = useState(initialStep);
  // Track the highest step that has been completed (allows navigation to any step <= highestStep + 1)
  const [highestCompletedStep, setHighestCompletedStepState] = useState(
    initialStep > 1 ? initialStep - 1 : 0
  );
  // Keep a ref for the latest value to avoid stale closures in async functions
  const highestCompletedStepRef = useRef(highestCompletedStep);
  const setHighestCompletedStep = (step: number) => {
    highestCompletedStepRef.current = step;
    setHighestCompletedStepState(step);
  };

  // Hydration tracking ref - declared early so navigation functions can access it
  const hydratedRef = useRef(false);


  // Store onStepChange in a ref to avoid it causing re-renders
  const onStepChangeRef = useRef(onStepChange);
  onStepChangeRef.current = onStepChange;

  // Wrapper to update step and sync to URL
  const setCurrentStep = useCallback((step: number) => {
    setCurrentStepState(step);
    // Use ref to avoid dependency on onStepChange
    onStepChangeRef.current?.(step);
  }, []);

  const [quoteData, setQuoteData] = useState<QuoteData>({
    clientType: null,
    selectedEntity: null,
    effectiveDate: null,
    coverageTypes: [],
    selectedEmployees: [],
    selectedDependents: [],
    selectedCarriers: {},
    selectedPlans: {},
    currentPlans: {},
    compareCurrentEnabled: {},
    rfpParameters: {},
  });

  const [draftQuoteId, setDraftQuoteId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Data queries
  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  const { data: prospects = [] } = useQuery<Prospect[]>({
    queryKey: ["/api/prospects"],
  });

  useEffect(() => {
    if (!initialClientId || existingQuote || quoteData.selectedEntity) return;
    const preselectedClient = clients.find((c) => c.id === initialClientId);
    if (preselectedClient) {
      setQuoteData((prev) => ({
        ...prev,
        clientType: "client",
        selectedEntity: preselectedClient,
      }));
    }
  }, [initialClientId, existingQuote, quoteData.selectedEntity, clients]);

  useEffect(() => {
    if (!initialProspectId || existingQuote || quoteData.selectedEntity) return;
    const preselectedProspect = prospects.find((p) => p.id === initialProspectId);
    if (preselectedProspect) {
      setQuoteData((prev) => ({
        ...prev,
        clientType: "prospect",
        selectedEntity: preselectedProspect,
      }));
    }
  }, [initialProspectId, existingQuote, quoteData.selectedEntity, prospects]);

  // Get clientId/prospectId from quoteData OR existingQuote
  const clientId =
    quoteData.selectedEntity?.id && quoteData.clientType === "client"
      ? quoteData.selectedEntity.id
      : existingQuote?.clientId ?? null;
  const prospectId =
    quoteData.selectedEntity?.id && quoteData.clientType === "prospect"
      ? quoteData.selectedEntity.id
      : existingQuote?.prospectId ?? null;

  // Fetch employees - simple query based on clientId or prospectId
  const { data: employees = [] } = useQuery<Employee[]>({
    queryKey: ["/api/employees", { clientId, prospectId }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (clientId) params.set("clientId", String(clientId));
      if (prospectId) params.set("prospectId", String(prospectId));
      const res = await fetch(`/api/employees?${params}`);
      if (!res.ok) throw new Error("Failed to fetch employees");
      const data = await res.json();
      console.log("[useQuoteWizard] Fetched employees:", { clientId, prospectId, count: data.length, data });
      return data;
    },
    enabled: !!(clientId || prospectId),
  });

  const sortedEmployees = useMemo(() => {
    return sortEmployeesByFullName(employees);
  }, [employees]);

  // State for Word & Brown plan search
  const [planSearchQueries, setPlanSearchQueries] = useState<
    Record<string, string>
  >({});
  const [isSearchingPlans, setIsSearchingPlans] = useState<
    Record<string, boolean>
  >({});
  const [wordBrownPlans, setWordBrownPlans] = useState<Record<string, any[]>>(
    {}
  );

  // State for plan filters (arrays for multi-select)
  const [planFilters, setPlanFilters] = useState<
    Record<
      string,
      {
        carriers: string[];
        metalTiers: string[];
        networkTypes: string[];
        planTypes: string[];
      }
    >
  >({});

  // State for rate selection dialog
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [selectedPlanForRates, setSelectedPlanForRates] = useState<any>(null);
  const [selectedAreaIndex, setSelectedAreaIndex] = useState(0);
  const [areaSearchTerm, setAreaSearchTerm] = useState("");
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const [planAreaRates, setPlanAreaRates] = useState<
    { label: string; rates: any[] }[]
  >([]);

  // Fetch existing plans from database
  // clientId decides each plan's `hasRates`: this client's own rates count,
  // another client's don't. A prospect sends none and gets shelf rates only.
  // The default queryFn turns queryKey[1] into query params and skips nulls.
  const { data: existingPlans = [] } = useQuery<PlanWithCarrier[]>({
    queryKey: ["/api/plans", { clientId }],
  });

  // This client's actual active plans (for pre-populating the current-plan
  // picker in Step 5) — only meaningful for an existing client; a prospect
  // was never enrolled anywhere, so there's nothing to fetch.
  const { data: clientCurrentPlans = [] } = useQuery<any[]>({
    queryKey: ["/api/client-plans", { clientId }],
    enabled: quoteData.clientType === "client" && !!clientId,
  });

  // Fetch dependents - simple query based on clientId or prospectId
  const { data: dependents = [] } = useQuery<Dependent[]>({
    queryKey: ["/api/dependents", { clientId, prospectId }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (clientId) params.set("clientId", String(clientId));
      if (prospectId) params.set("prospectId", String(prospectId));
      const res = await fetch(`/api/dependents?${params}`);
      if (!res.ok) throw new Error("Failed to fetch dependents");
      return res.json();
    },
    enabled: !!(clientId || prospectId),
  });

  const relevantEmployees = sortedEmployees;

  // Fetch carriers
  const { data: carriers = [] } = useQuery<Carrier[]>({
    queryKey: ["/api/carriers"],
  });

  // Carriers that can actually be quoted for the entity's state: at least one
  // active library plan offered there WITH rates, per coverage type. Drives the
  // carrier step so carriers with no data (or out-of-state) are not listed.
  const quotingState = quoteData.selectedEntity?.state || null;
  const quotingRatingAreaId = quoteData.confirmedRatingArea?.id ?? null;
  const quotingEffectiveDate = quoteData.effectiveDate
    ? format(quoteData.effectiveDate, "yyyy-MM-dd")
    : null;
  const { data: quotableCarriers = [], isLoading: quotableCarriersLoading } = useQuery<
    { id: number; carrierId: string; carrierName: string; quotableTypes: string[] }[]
  >({
    queryKey: ["/api/plan-source/carriers", quotingState, quotingRatingAreaId, quotingEffectiveDate, clientId],
    enabled: !!quotingState,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const params = new URLSearchParams({ state: quotingState! });
      if (quotingRatingAreaId != null) params.set("ratingAreaId", String(quotingRatingAreaId));
      if (quotingEffectiveDate) params.set("effectiveDate", quotingEffectiveDate);
      // A carrier this client only has custom plans with still has to be listed.
      if (clientId) params.set("clientId", String(clientId));
      const res = await fetch(`/api/plan-source/carriers?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load quotable carriers");
      const json = await res.json();
      return json?.carriers ?? [];
    },
  });

  // Fetch directory contacts for carrier sales contacts
  const { data: directoryContacts = [], refetch: refetchDirectoryContacts } = useQuery<DirectoryContact[]>({
    queryKey: ["/api/directory-contacts"],
  });

  // The wholesaler FIRMS. Step 4 ticks these; step 6 expands each into all of
  // its sales contacts. carrierIds says which carriers each one can be asked
  // for — the endpoint always sends it, so the type must say so too.
  const { data: generalAgencies = [] } = useQuery<GeneralAgencyWithCarriers[]>({
    queryKey: ["/api/general-agencies"],
  });

  // Calculate premiums for all selected plans
  const premiumSummary = useQuotePremiumCalculation({
    selectedPlans: quoteData.selectedPlans,
    selectedEmployees: quoteData.selectedEmployees,
    selectedDependents: quoteData.selectedDependents,
    employees: sortedEmployees,
    dependents: dependents,
    effectiveDate: quoteData.effectiveDate,
    clientId:
      quoteData.clientType === "client" ? quoteData.selectedEntity?.id : null,
    ratingAreaId: quoteData.confirmedRatingArea?.id ?? null,
    enabled: currentStep >= 5 && quoteData.selectedEmployees.length > 0,
  });

  // currentPlans is one plan per coverage type (not a list); the calc hook
  // wants the same shape as selectedPlans, so wrap each non-null pick in a
  // 1-element array. Mirrors premiumSummary above field-for-field so the
  // comparison table in Step 5 is computed the same way as the renewal side.
  const currentPlansForCalc = useMemo(() => {
    const result: Record<string, PlanWithCarrier[]> = {};
    Object.entries(quoteData.currentPlans).forEach(([coverageType, plan]) => {
      if (plan) result[coverageType] = [plan];
    });
    return result;
  }, [quoteData.currentPlans]);

  const currentPremiumSummary = useQuotePremiumCalculation({
    selectedPlans: currentPlansForCalc,
    selectedEmployees: quoteData.selectedEmployees,
    selectedDependents: quoteData.selectedDependents,
    employees: sortedEmployees,
    dependents: dependents,
    effectiveDate: quoteData.effectiveDate,
    clientId:
      quoteData.clientType === "client" ? quoteData.selectedEntity?.id : null,
    ratingAreaId: quoteData.confirmedRatingArea?.id ?? null,
    enabled: currentStep >= 5 && quoteData.selectedEmployees.length > 0,
  });

  // Update quote data helper
  const updateQuoteData = (updates: Partial<QuoteData>) => {
    setQuoteData((prev) => ({ ...prev, ...updates }));
  };

  // Search Word & Brown plans (and mock plans for non-health types)
  const searchWordBrownPlans = async (
    coverageType: string,
    searchQuery: string = ""
  ) => {
    const selectedCarrierIds = quoteData.selectedCarriers[coverageType] || [];
    if (selectedCarrierIds.length === 0) return;

    setIsSearchingPlans((prev) => ({ ...prev, [coverageType]: true }));

    try {
      const state = quoteData.selectedEntity?.state || "CA";
      const effectiveDate = quoteData.effectiveDate
        ? format(quoteData.effectiveDate, "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");

      const allPlans: any[] = [];

      // For non-health types, also fetch from mock API (if mock carrier is selected)
      const normalizedType = normalizePlanType(coverageType);
      const isNonHealthType = ["dental", "vision", "life", "disability"].includes(normalizedType);

      if (isNonHealthType) {
        // Check if any selected carrier has the mock carrier wordBrownId
        for (const localCarrierId of selectedCarrierIds) {
          const carrier = carriers.find((c) => c.id === localCarrierId);
          if (carrier?.wordBrownId === "MOCK-CARRIER-001") {
            try {
              const mockRes = await fetch(
                `/api/mock-plans/plans?type=${normalizedType}&carrierId=${localCarrierId}&state=${state}&effectiveDate=${effectiveDate}`
              );
              if (mockRes.ok) {
                const mockResponse = await mockRes.json();
                console.log("[Mock Plans API] Response for type:", normalizedType, mockResponse);
                const mockPlans = mockResponse?.plans || [];

                // Filter by search query if provided
                const filteredMockPlans = searchQuery
                  ? mockPlans.filter((p: any) =>
                      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.carrier?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.planCode?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                  : mockPlans;

                // Add mock carrier info
                filteredMockPlans.forEach((p: any) => {
                  allPlans.push({
                    ...p,
                    localCarrierId,
                    carrierName: carrier.name,
                    source: "mock",
                  });
                });
                console.log("[Mock Plans API] Added", filteredMockPlans.length, "mock plans for", normalizedType);
              }
            } catch (mockError) {
              console.error("Failed to fetch mock plans:", mockError);
            }
          }
        }
      }

      // Library plans per selected carrier (skip mock carrier — handled above).
      // Carriers are identified by their legacy provider id when they have one,
      // else by our internal id; the plan-source endpoint resolves both, so
      // manual carriers without a W&B id are quotable too.
      for (let i = 0; i < selectedCarrierIds.length; i++) {
        const localCarrierId = selectedCarrierIds[i];
        const carrier = carriers.find((c) => c.id === localCarrierId);
        if (!carrier) continue;
        if (carrier.wordBrownId === "MOCK-CARRIER-001") continue;

        // Plans come from the Plan Library (our DB) via the plan-source layer.
        // A search query is applied to those library results client-side rather
        // than calling W&B live — the carrier loop already scopes by carrier, so
        // search == browse + substring filter (DEV-202).
        const params: any = {
          carrierId: carrier.wordBrownId ?? String(carrier.id),
          state,
          effectiveDate,
          // Only plans that can be priced for the client's confirmed rating area
          ratingAreaId: quoteData.confirmedRatingArea?.id ?? undefined,
        };

        try {
          const qs = new URLSearchParams({
            carrierId: String(params.carrierId),
            state: params.state,
            effectiveDate: params.effectiveDate,
          });
          if (params.ratingAreaId != null) qs.set("ratingAreaId", String(params.ratingAreaId));
          // Lets the server mark plans this client can't be priced on.
          if (clientId) qs.set("clientId", String(clientId));
          const res = await apiRequest("GET", `/api/plan-source/plans?${qs.toString()}`);
          const response: any = await res.json();
          console.log("[Plan Source] Response for carrier:", carrier.name, { params, response });
          const plansList = response?.success ? response.plans || [] : [];

          // Library-backed search: match the query against the same fields the
          // old live W&B search did (name / code / network / metal / carrier).
          const q = searchQuery?.trim().toLowerCase();
          const matchesQuery = (p: any) =>
            !q ||
            [p.name, p.planName, p.planCode, p.networkType, p.metalTier, carrier.name]
              .some((v: any) => String(v ?? "").toLowerCase().includes(q));

          if (plansList.length > 0) {
            // Log all plan types received
            const planTypes = Array.from(new Set(plansList.map((p: any) => p.type)));
            console.log("[Plan Source] Plan types in response:", planTypes, "| Filtering for:", coverageType);

            const filteredPlans = plansList
              .filter(
                (p: any) =>
                  matchesQuery(p) &&
                  normalizePlanType(p.type) === normalizePlanType(coverageType)
              )
              .map((p: any) => ({
                ...p,
                localCarrierId,
                carrierName: carrier.name,
                source: "word_brown",
              }));
            console.log("[Plan Source] Filtered plans:", { total: plansList.length, kept: filteredPlans.length, filteredPlans });
            allPlans.push(...filteredPlans);
          }
        } catch (carrierError) {
          console.error(
            `Failed to fetch plans for carrier ${carrier.name}:`,
            carrierError
          );
        }
      }

      setWordBrownPlans((prev) => ({ ...prev, [coverageType]: allPlans }));
    } catch (error) {
      console.error("Error searching library plans:", error);
      toast({
        title: "Plan Search Error",
        description: "Failed to search plans from the Plan Library",
        variant: "destructive",
      });
    } finally {
      setIsSearchingPlans((prev) => ({ ...prev, [coverageType]: false }));
    }
  };

  // Auto-load library plans when carriers are selected and we reach step 5.
  // Plans come from our DB now (no live W&B call), so resumed quotes load the
  // same list as new ones instead of a cache-only subset.
  useEffect(() => {
    if (currentStep === 5) {
      quoteData.coverageTypes.forEach((coverageType) => {
        const selectedCarrierIds =
          quoteData.selectedCarriers[coverageType] || [];
        if (selectedCarrierIds.length > 0 && !wordBrownPlans[coverageType]) {
          searchWordBrownPlans(coverageType);
        }
      });
    }
  }, [currentStep, quoteData.coverageTypes, quoteData.selectedCarriers]);

  // Manual refresh function for all coverage types
  const refreshAllPlans = () => {
    quoteData.coverageTypes.forEach((coverageType) => {
      const selectedCarrierIds =
        quoteData.selectedCarriers[coverageType] || [];
      if (selectedCarrierIds.length > 0) {
        searchWordBrownPlans(coverageType);
      }
    });
  };

  // Helper functions for plan processing
  const normalizeNetworkType = (
    networkType: string | null | undefined
  ): string | null => {
    if (!networkType) return null;
    const normalized = networkType.toUpperCase();
    if (normalized.includes("HMO")) return "HMO";
    if (normalized.includes("PPO")) return "PPO";
    if (normalized.includes("EPO")) return "EPO";
    if (normalized.includes("POS")) return "POS";
    if (normalized.includes("HDHP") || normalized.includes("HIGH DEDUCTIBLE"))
      return "HDHP";
    return null;
  };

  const extractMetalTier = (plan: any): string | null => {
    const existingTier = plan.metalTier || plan.MetalTier || plan.tier;
    if (existingTier) {
      const normalized = existingTier.toString().toLowerCase();
      if (normalized.includes("bronze")) return "Bronze";
      if (normalized.includes("silver")) return "Silver";
      if (normalized.includes("gold")) return "Gold";
      if (normalized.includes("platinum")) return "Platinum";
    }

    const rawName = plan.name || plan.PlanName || plan.planName || "";
    if (!rawName) return null;

    const name = String(rawName).toLowerCase();
    if (/\bplatinum\b/.test(name) || /\bplat\b/.test(name)) return "Platinum";
    if (/\bgold\b/.test(name)) return "Gold";
    if (/\bsilver\b/.test(name)) return "Silver";
    if (/\bbronze\b/.test(name)) return "Bronze";

    const firstToken = name.trim().split(/[\s\-_/]+/)[0];
    if (/^p\b/.test(firstToken)) return "Platinum";
    if (/^g\b/.test(firstToken)) return "Gold";
    if (/^s\b/.test(firstToken)) return "Silver";
    if (/^b\b/.test(firstToken)) return "Bronze";

    return null;
  };

  // Persist draft quote
  const persistDraft = async (stepToSave: number) => {
    try {
      if (!quoteData.selectedEntity) return;

      // When reopening a quote that already had plans, don't save until those
      // plans have been loaded back into state. Saving too early writes an
      // empty selectedPlans, and the server then marks every plan on the quote
      // as removed (syncQuotePlans([])) — the real cause of "plans disappear".
      if (existingQuote && !plansHydratedRef.current) {
        const savedPlans: any =
          (existingQuote as any).wizardState?.selectedPlans || {};
        const hadPlans = Object.values(savedPlans).some(
          (arr: any) => Array.isArray(arr) && arr.length > 0
        );
        if (hadPlans) return;
      }
      // Same race, for currentPlans: don't save until a resumed current plan
      // has been loaded back into state, or the save overwrites it with {}.
      if (existingQuote && !currentPlansHydratedRef.current) {
        const savedCurrentPlans: any =
          (existingQuote as any).wizardState?.currentPlans || {};
        const hadCurrentPlans = Object.values(savedCurrentPlans).some(
          (item: any) => item != null
        );
        if (hadCurrentPlans) return;
      }

      setSaving(true);

      const planType =
        quoteData.coverageTypes.length === 1
          ? quoteData.coverageTypes[0]
          : quoteData.coverageTypes.length > 1
          ? "full_package"
          : "health";

      const selectedPlanIds: Record<string, number[]> = Object.fromEntries(
        Object.entries(quoteData.selectedPlans || {}).map(([k, arr]) => [
          k,
          (arr as any[]).map((p: any) => p.id).filter(Boolean),
        ])
      );

      // Calculate the highest step that should be accessible after this save
      // Use ref to get the latest value and avoid stale closures
      const newHighestCompleted = Math.max(
        highestCompletedStepRef.current,
        stepToSave - 1
      );

      // Collect all unique carrier IDs from selectedCarriers
      const allCarrierIds = Array.from(
        new Set(
          Object.values(quoteData.selectedCarriers || {})
            .flat()
            .filter(Boolean)
        )
      );

      const payload: any = {
        companyName: quoteData.selectedEntity?.name || "Draft",
        employeeCount: quoteData.selectedEmployees?.length || 0,
        planType,
        status: "draft",
        effectiveDate: quoteData.effectiveDate
          ? (parseDateLocal(quoteData.effectiveDate as any) ?? new Date()).toISOString()
          : null,
        clientId:
          quoteData.clientType === "client"
            ? quoteData.selectedEntity?.id
            : null,
        prospectId:
          quoteData.clientType === "prospect"
            ? quoteData.selectedEntity?.id
            : null,
        currentStep: stepToSave,
        wizardState: buildWizardState(quoteData, {
          mode,
          highestCompletedStep: newHighestCompleted,
          premiumSummary,
          currentPremiumSummary,
        }),
        // New fields for type and metadata (11.2, 11.3)
        type: mode, // 'quote' or 'rfp'
        coverageTypes: quoteData.coverageTypes || [],
        carrierIds: allCarrierIds,
        // Estimated = one plan per coverage type (what the client would pay),
        // matching what the share step stores — not the sum of every option.
        totalMonthlyPremium: premiumSummary.estimatedMonthlyPremium || null,
        totalAnnualPremium: premiumSummary.estimatedAnnualPremium || null,
      };

      if (!draftQuoteId) {
        const res = await apiRequest("POST", "/api/quotes", payload);
        if (!res.ok) throw new Error("Failed to create draft quote");
        const created = await res.json();
        setDraftQuoteId(created.id);
        // Sync draft ID to URL for persistence across reloads
        onDraftIdChange?.(created.id);
      } else {
        const res = await apiRequest(
          "PUT",
          `/api/quotes/${draftQuoteId}`,
          payload
        );
        if (!res.ok) throw new Error("Failed to update draft quote");
      }

      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
    } catch (e: any) {
      console.error(e);
      toast({
        title: "Save failed",
        description: e?.message || "Could not save draft",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Navigation
  const nextStep = async () => {
    if (currentStep < STEPS.length) {
      const stepWeAreGoingTo = Math.min(currentStep + 1, STEPS.length);
      // Update highest completed step when moving forward
      // Use ref to get latest value and avoid stale closure
      const newHighestCompleted = Math.max(highestCompletedStepRef.current, currentStep);
      setHighestCompletedStep(newHighestCompleted);
      if (currentStep < 9) {
        await persistDraft(stepWeAreGoingTo);
      }
      setCurrentStep(stepWeAreGoingTo);
    }
  };

  const prevStep = async () => {
    if (currentStep > 1) {
      if (
        currentStep === 4 &&
        Object.keys(quoteData.selectedCarriers).length > 0
      ) {
        if (
          !confirm("Going back will clear your carrier selections. Continue?")
        )
          return;
        updateQuoteData({ selectedCarriers: {}, selectedPlans: {} });
      } else if (
        currentStep === 5 &&
        Object.keys(quoteData.selectedPlans).length > 0
      ) {
        if (!confirm("Going back will clear your plan selections. Continue?"))
          return;
        updateQuoteData({ selectedPlans: {} });
      } else if (
        currentStep === 3 &&
        (quoteData.selectedEmployees.length > 0 ||
          quoteData.selectedDependents.length > 0)
      ) {
        if (
          !confirm(
            "Going back may affect your employee and dependent selections. Continue?"
          )
        )
          return;
      }
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      // Save the current step position so reload preserves it
      // Only save if hydration is complete to avoid saving stale data
      if (draftQuoteId && hydratedRef.current) {
        await persistDraft(newStep);
      }
    }
  };

  // Navigate to a specific step (only allowed if step is accessible)
  const goToStep = async (step: number) => {
    // Can navigate to any step that has been completed or the next step after highest completed
    // Use ref to get latest value and avoid stale closure
    if (step >= 1 && step <= highestCompletedStepRef.current + 1 && step <= STEPS.length) {
      setCurrentStep(step);
      // Save the current step position so reload preserves it
      // Only save if hydration is complete to avoid saving stale data
      if (draftQuoteId && hydratedRef.current) {
        await persistDraft(step);
      }
    }
  };

  // Check if a step is accessible for navigation
  const isStepAccessible = (step: number) => {
    return (
      step >= 1 && step <= highestCompletedStep + 1 && step <= STEPS.length
    );
  };

  // Helper to check if entity has required fields (SIC code and ZIP code)
  const getEntityValidation = () => {
    const entity = quoteData.selectedEntity;
    if (!entity) return { isValid: false, missingFields: [] as string[] };

    const missingFields: string[] = [];

    // Check ZIP code
    if (!entity.zipCode || entity.zipCode.trim() === "") {
      missingFields.push("ZIP Code");
    }

    // Check SIC code (for clients, check sicCodeId; for prospects, check sic or sicCodeId)
    const hasSicCode = entity.sicCodeId || entity.sic;
    if (!hasSicCode) {
      missingFields.push("SIC Code");
    }

    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  };

  // Can proceed validation - different logic for Quote vs RFP mode
  const canProceed = () => {
    if (isRFPMode) {
      // RFP Mode: 1=Entity, 2=Coverage, 3=Census, 4=Carriers, 5=RFP Params, 6=RFP Preview
      switch (currentStep) {
        case 1: {
          const hasBasicRequirements =
            quoteData.clientType &&
            quoteData.selectedEntity &&
            quoteData.effectiveDate;
          if (!hasBasicRequirements) return false;
          // Validate entity has SIC code and ZIP code
          const { isValid } = getEntityValidation();
          return isValid;
        }
        case 2:
          return quoteData.coverageTypes.length > 0;
        case 3:
          return quoteData.selectedEmployees.length > 0;
        case 4: {
          const hasCarriers = Object.values(quoteData.selectedCarriers).some(
            (arr) => arr.length > 0
          );
          // Checked first: with no carriers picked, every GA covers nothing and
          // the GA problems below would all fire with nonsense wording.
          if (!hasCarriers) return false;
          // Going through a GA needs somebody to send to, and every picked
          // carrier to have a GA that can actually approach it. Step 4 renders
          // the same list as text — the Next button has no tooltip.
          if (quoteData.rfpSendVia === "ga") {
            return (
              findGaSelectionProblems({
                sendVia: quoteData.rfpSendVia,
                selectedGeneralAgencyIds: quoteData.rfpGeneralAgencyIds ?? [],
                asks: quoteData.rfpGeneralAgencyAsks ?? [],
              }).length === 0
            );
          }
          return true;
        }
        case 5: // RFP Parameters
          return true;
        case 6: // RFP Preview (final step)
          return true;
        default:
          return false;
      }
    }

    // Quote Mode: 7-step validation (no RFP steps)
    // 1=Entity, 2=Coverage, 3=Census, 4=Carriers, 5=Plan Selection, 6=Summary, 7=Generate
    switch (currentStep) {
      case 1: {
        const hasBasicRequirements =
          quoteData.clientType &&
          quoteData.selectedEntity &&
          quoteData.effectiveDate;
        if (!hasBasicRequirements) return false;
        // Validate entity has SIC code and ZIP code
        const { isValid } = getEntityValidation();
        if (!isValid) return false;
        // Validate that a rating area is confirmed (indicates state is supported for quoting)
        if (!quoteData.confirmedRatingArea) return false;
        return true;
      }
      case 2:
        return quoteData.coverageTypes.length > 0;
      case 3:
        return quoteData.selectedEmployees.length > 0;
      case 4:
        return Object.values(quoteData.selectedCarriers).some(
          (arr) => arr.length > 0
        );
      case 5:
        return Object.values(quoteData.selectedPlans).some(
          (arr) => arr.length > 0
        );
      case 6: // Quote Summary
        return true;
      case 7: // Generate Quote
        return true;
      default:
        return false;
    }
  };

  // Hydration for existing quote (resume/edit)
  const plansHydratedRef = useRef(false);
  const selectedPlanIdsRef = useRef<Record<string, number[]>>({});
  // currentPlans is one plan per type, not a list, so it gets its own ref
  // pair rather than reusing selectedPlans' — same async id-resolution need,
  // different shape.
  const currentPlansHydratedRef = useRef(false);
  const currentPlanIdsRef = useRef<Record<string, number | object | null>>({});
  const carriersHydratedRef = useRef(false);
  const selectedCarrierValuesRef = useRef<Record<string, (number | string)[]>>(
    {}
  );

  // Set refs early - before the main hydration effect waits for entity data
  // This ensures carrier/plan hydration effects have access to the saved data
  useEffect(() => {
    if (!existingQuote) return;
    const ws: any = (existingQuote as any).wizardState || {};
    // Only set if not already set (to avoid overwriting during re-renders)
    if (Object.keys(selectedPlanIdsRef.current).length === 0) {
      selectedPlanIdsRef.current = ws.selectedPlans || {};
    }
    if (Object.keys(currentPlanIdsRef.current).length === 0) {
      currentPlanIdsRef.current = ws.currentPlans || {};
    }
    if (Object.keys(selectedCarrierValuesRef.current).length === 0) {
      selectedCarrierValuesRef.current = ws.selectedCarriers || {};
    }
  }, [existingQuote]);

  useEffect(() => {
    if (!existingQuote || hydratedRef.current) return;

    const ws: any = (existingQuote as any).wizardState || {};
    const inferredClientType: QuoteData["clientType"] =
      ws.clientType ||
      (existingQuote.clientId
        ? "client"
        : existingQuote.prospectId
        ? "prospect"
        : null);
    const selectedEntityId =
      ws.selectedEntityId ??
      existingQuote.clientId ??
      existingQuote.prospectId ??
      null;

    const entityList =
      inferredClientType === "client"
        ? clients
        : inferredClientType === "prospect"
        ? prospects
        : [];

    // Wait for entity data to be loaded before hydrating
    // This ensures we have the full entity object with all properties (like state, zipCode, etc.)
    if (selectedEntityId && entityList.length === 0) {
      // Data not loaded yet, wait for next render
      return;
    }

    const foundEntity =
      entityList.find((e: any) => e.id === selectedEntityId) || null;

    // If we have an entity ID but couldn't find it in the list, create a minimal fallback
    // This shouldn't happen normally but handles edge cases
    const entityToUse =
      foundEntity ||
      (selectedEntityId
        ? { id: selectedEntityId, name: existingQuote.companyName }
        : null);

    const eff =
      ws.effectiveDate ?? (existingQuote as any).effectiveDate ?? null;
    const effectiveDate = eff ? parseDateLocal(eff) : null;

    const coverageTypes =
      Array.isArray(ws.coverageTypes) && ws.coverageTypes.length > 0
        ? ws.coverageTypes
        : typeof existingQuote.planType === "string" &&
          [
            "health",
            "medical",
            "dental",
            "vision",
            "life",
            "disability",
            "voluntary",
          ].includes(existingQuote.planType)
        ? [existingQuote.planType]
        : [];

    // Don't map carriers here - let the dedicated carrier hydration effect handle it
    // This prevents race conditions where carriers aren't loaded yet

    setQuoteData((prev) => ({
      clientType: inferredClientType,
      selectedEntity: entityToUse,
      effectiveDate,
      coverageTypes,
      selectedEmployees: ws.selectedEmployees || [],
      selectedDependents: ws.selectedDependents || [],
      // Preserve selections already rebuilt by the carrier/plan hydration
      // effects. Those effects can run before this one (query resolution
      // order), so resetting to {} here would wipe a resumed quote's plans.
      selectedCarriers: Object.keys(prev.selectedCarriers).length
        ? prev.selectedCarriers
        : {},
      selectedPlans: Object.keys(prev.selectedPlans).length
        ? prev.selectedPlans
        : {},
      currentPlans: Object.keys(prev.currentPlans || {}).length
        ? prev.currentPlans
        : {},
      // No id-resolution step (unlike currentPlans), so it can be restored
      // directly here instead of through a dedicated async hydration effect.
      compareCurrentEnabled: ws.compareCurrentEnabled || {},
      rfpParameters: ws.rfpParameters || {},
      rfpSendVia: ws.rfpSendVia || "carriers",
      // Drafts predating general_agencies hold contact ids under the old key,
      // so they resume in GA mode with nothing picked. Step 4 blocks Next.
      rfpGeneralAgencyIds: ws.rfpGeneralAgencyIds || [],
      rfpGeneralAgencyAsks: ws.rfpGeneralAgencyAsks || [],
      rfpRespondBy: ws.rfpRespondBy || null,
      rfpEmailGreeting: ws.rfpEmailGreeting || null,
      rfpEmailIntro: ws.rfpEmailIntro || null,
      rfpEmailClosing: ws.rfpEmailClosing || null,
      rfpEmailSignature: ws.rfpEmailSignature || null,
      confirmedRatingArea: ws.confirmedRatingArea || null,
      proposalSettings: ws.proposalSettings || (existingQuote as any).proposalSettings || undefined,
      contribution: ws.contribution ? normalizeQuoteContribution(ws.contribution) : undefined,
    }));

    setDraftQuoteId(existingQuote.id);
    const savedStep = (existingQuote as any).currentStep || 1;
    setCurrentStep(savedStep);
    // Restore highest completed step from wizard state, or calculate from saved step
    const savedHighestCompleted = ws.highestCompletedStep;
    if (
      typeof savedHighestCompleted === "number" &&
      savedHighestCompleted >= 0
    ) {
      setHighestCompletedStep(savedHighestCompleted);
    } else {
      // Fallback: assume all steps up to saved step are completed
      setHighestCompletedStep(savedStep > 1 ? savedStep - 1 : 0);
    }
    hydratedRef.current = true;
  }, [existingQuote, clients, prospects, carriers]);

  useEffect(() => {
    if (!existingQuote || plansHydratedRef.current) return;
    const savedPlans = selectedPlanIdsRef.current || {};
    // Nothing saved to restore — mark hydrated so persistDraft's resume guard
    // stops blocking saves for this (planless) quote.
    if (Object.keys(savedPlans).length === 0) {
      plansHydratedRef.current = true;
      return;
    }
    // A bare id (number) needs the library to resolve into a plan object, so
    // wait for /api/plans. When every saved entry is already a full object we
    // restore from those directly and never block on the library load.
    const needsLibrary = Object.values(savedPlans).some((arr: any) =>
      (arr as any[]).some((it: any) => typeof it === "number")
    );
    if (needsLibrary && (!existingPlans || existingPlans.length === 0)) return;

    const rebuilt: Record<string, any[]> = {};
    for (const [type, items] of Object.entries(savedPlans)) {
      // Handle both formats: array of IDs (numbers) or array of plan objects
      const itemsArray = items as any[];
      rebuilt[type] = itemsArray
        .map((item: any) => {
          const id = typeof item === "number" ? item : (item?.id || item);
          if (!id) return null;
          // Match by id only (ids are unique). Fall back to the saved plan
          // object when it isn't in /api/plans (e.g. dropped from the library
          // scope, or a type-key mismatch) so a resumed plan never silently
          // disappears.
          return (
            existingPlans.find((p: any) => p.id === id) ||
            (typeof item === "object" ? item : null)
          );
        })
        .filter(Boolean) as any[];
    }
    setQuoteData((prev) => ({ ...prev, selectedPlans: rebuilt }));
    plansHydratedRef.current = true;
  }, [existingPlans, existingQuote]);

  useEffect(() => {
    if (!existingQuote || currentPlansHydratedRef.current) return;
    const savedCurrentPlans = currentPlanIdsRef.current || {};
    if (Object.keys(savedCurrentPlans).length === 0) {
      currentPlansHydratedRef.current = true;
      return;
    }
    const needsLibrary = Object.values(savedCurrentPlans).some(
      (item: any) => typeof item === "number"
    );
    if (needsLibrary && (!existingPlans || existingPlans.length === 0)) return;

    const rebuilt: Record<string, any | null> = {};
    for (const [type, item] of Object.entries(savedCurrentPlans)) {
      if (item == null) {
        rebuilt[type] = null;
        continue;
      }
      const id = typeof item === "number" ? item : (item as any)?.id || item;
      rebuilt[type] =
        existingPlans.find((p: any) => p.id === id) ||
        (typeof item === "object" ? item : null);
    }
    setQuoteData((prev) => ({ ...prev, currentPlans: rebuilt }));
    currentPlansHydratedRef.current = true;
  }, [existingPlans, existingQuote]);

  useEffect(() => {
    if (!existingQuote || carriersHydratedRef.current) return;
    // Wait for carriers data to be loaded
    if (!carriers || carriers.length === 0) return;

    const rawMap = selectedCarrierValuesRef.current || {};

    if (!rawMap || Object.keys(rawMap).length === 0) {
      // No saved carriers - check if wizardState has carriers that haven't been loaded to ref yet
      const ws: any = (existingQuote as any).wizardState || {};
      if (ws.selectedCarriers && Object.keys(ws.selectedCarriers).length > 0) {
        // wizardState has carriers but ref is empty - use wizardState directly
        selectedCarrierValuesRef.current = ws.selectedCarriers;
      } else {
        carriersHydratedRef.current = true;
        return;
      }
    }

    const byName = new Map<string, number>();
    carriers.forEach((c) => byName.set(c.name ?? "", c.id));
    const mapped: Record<string, number[]> = {};
    for (const [type, arr] of Object.entries(selectedCarrierValuesRef.current)) {
      const ids = (arr as any[])
        .map((v) => (typeof v === "number" ? v : byName.get(String(v))))
        .filter((v): v is number => typeof v === "number");
      mapped[type] = ids;
    }
    // Only set if current selectedCarriers is empty (don't overwrite user changes)
    setQuoteData((prev) => {
      const hasExistingSelections = Object.values(prev.selectedCarriers).some(arr => arr.length > 0);
      if (hasExistingSelections) {
        return prev;
      }
      return { ...prev, selectedCarriers: mapped };
    });
    carriersHydratedRef.current = true;
  }, [carriers, existingQuote]);

  return {
    // Mode
    mode,
    isRFPMode,
    steps: STEPS,

    // State
    currentStep,
    quoteData,
    saving,
    draftQuoteId,

    // Data
    clients,
    prospects,
    employees: sortedEmployees,
    dependents,
    carriers,
    quotableCarriers,
    quotableCarriersLoading,
    directoryContacts,
    refetchDirectoryContacts,
    generalAgencies,
    existingPlans,
    clientCurrentPlans,
    relevantEmployees,
    premiumSummary,
    currentPremiumSummary,

    // Word & Brown
    wordBrownPlans,
    planSearchQueries,
    isSearchingPlans,
    planFilters,

    // Rate dialog
    rateDialogOpen,
    selectedPlanForRates,
    selectedAreaIndex,
    areaSearchTerm,
    isCreatingPlan,
    isFetchingRates,
    planAreaRates,

    // Navigation state
    highestCompletedStep,

    // Validation
    getEntityValidation,

    // Actions
    updateQuoteData,
    setCurrentStep,
    nextStep,
    prevStep,
    goToStep,
    isStepAccessible,
    canProceed,
    searchWordBrownPlans,
    refreshAllPlans,
    setPlanSearchQueries,
    setPlanFilters,
    setRateDialogOpen,
    setSelectedPlanForRates,
    setSelectedAreaIndex,
    setAreaSearchTerm,
    setIsCreatingPlan,
    setIsFetchingRates,
    setPlanAreaRates,
    persistDraft,

    // Helpers
    normalizeNetworkType,
    extractMetalTier,
    toast,
    queryClient,
  };
}

export type QuoteWizardContext = ReturnType<typeof useQuoteWizard>;
